import React from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';
import { Logo } from '@/components/ui/Logo';
import { Loader2, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';

type ViewState = 'confirming' | 'success' | 'timeout' | 'error';

export function CheckoutConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const source = searchParams.get('source'); // 'onboarding' | 'paywall'
  const [view, setView] = React.useState<ViewState>('confirming');
  const [elapsed, setElapsed] = React.useState(0);

  // Refs to guard against StrictMode double-mount and concurrent polls
  const abortRef = React.useRef(false);
  const resolvedRef = React.useRef(false);

  const POLL_INTERVAL = 2000;
  const TIMEOUT = 45000;

  // Separate effect for redirect — avoids stale navigate closure from StrictMode
  React.useEffect(() => {
    if (view !== 'success') return;
    const redirectTo = source === 'paywall'
      ? '/dashboard?checkout=success'
      : '/onboarding?checkout=success';
    const timer = setTimeout(() => {
      navigate(redirectTo, { replace: true });
    }, 1200);
    return () => clearTimeout(timer);
  }, [view, navigate, source]);

  React.useEffect(() => {
    if (!sessionId) {
      setView('error');
      return;
    }

    // Reset refs on mount (StrictMode remount resets these)
    abortRef.current = false;
    resolvedRef.current = false;

    // Elapsed seconds counter
    const timerHandle = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    const startTime = Date.now();
    let pollHandle: ReturnType<typeof setInterval> | null = null;

    const cleanup = () => {
      abortRef.current = true;
      clearInterval(timerHandle);
      if (pollHandle) clearInterval(pollHandle);
    };

    const autoLoginAndPopulateStores = async (orgId?: string) => {
      // Populate auth store from the httpOnly cookies the server just set
      try {
        await useAuthStore.getState().fetchMe(true);
      } catch {
        // If fetchMe fails, the user may need to sign in manually
      }

      if (orgId) {
        try {
          localStorage.setItem('ima_active_org_id', orgId);
          useOrgStore.getState().setActiveOrgId(orgId);
          await useOrgStore.getState().fetchOrgs();
        } catch {}
      }

      // Setting view to 'success' triggers the redirect effect above
      setView('success');
    };

    const poll = async () => {
      // Prevent concurrent resolution
      if (resolvedRef.current || abortRef.current) return;

      try {
        const res = await fetch(
          `/api/public/checkout-status?session_id=${encodeURIComponent(sessionId)}`,
          { credentials: 'include' },
        );
        const json = await res.json();
        const data = json?.data;

        if (abortRef.current || resolvedRef.current) return;

        if (data?.status === 'ready') {
          resolvedRef.current = true;
          cleanup();
          await autoLoginAndPopulateStores(data.orgId);
          return;
        }

        if (data?.status === 'already_claimed') {
          // Account already exists — try auto-login anyway (user may have refreshed)
          resolvedRef.current = true;
          cleanup();
          await autoLoginAndPopulateStores(data.orgId);
          return;
        }

        // Still pending — check timeout
        if (Date.now() - startTime > TIMEOUT) {
          resolvedRef.current = true;
          cleanup();
          setView('timeout');
        }
      } catch {
        // Network error — keep polling unless timeout
        if (Date.now() - startTime > TIMEOUT) {
          resolvedRef.current = true;
          cleanup();
          setView('timeout');
        }
      }
    };

    // Initial poll immediately, then interval
    poll();
    pollHandle = setInterval(poll, POLL_INTERVAL);

    return cleanup;
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <Logo size="xl" className="justify-center mb-2" />
          </Link>
        </div>

        {/* Card */}
        <div className="bg-background rounded-2xl shadow-xl overflow-hidden border p-8">
          {view === 'confirming' && (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-2">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <h2 className="text-xl font-semibold">Confirming your payment...</h2>
              <p className="text-sm text-muted-foreground">
                {elapsed > 8
                  ? "Almost there — just a few more seconds."
                  : elapsed > 3
                    ? "Setting up your workspace..."
                    : "Your payment was successful. We're getting everything ready."}
              </p>
              {/* Subtle progress dots */}
              <div className="flex items-center justify-center gap-1.5 pt-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-pulse" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-pulse" style={{ animationDelay: '300ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-pulse" style={{ animationDelay: '600ms' }} />
              </div>
            </div>
          )}

          {view === 'success' && (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-2">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold">You're all set!</h2>
              <p className="text-sm text-muted-foreground">
                Your account is ready. Redirecting you now...
              </p>
              <div className="flex items-center justify-center gap-2 text-primary">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Redirecting</span>
              </div>
            </div>
          )}

          {view === 'timeout' && (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-2">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold">Taking longer than expected</h2>
              <p className="text-sm text-muted-foreground">
                Your payment was successful, but account setup is taking a bit longer.
                You'll receive an email with login details shortly.
              </p>
              <div className="flex flex-col gap-2 mt-4">
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {view === 'error' && (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-2">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold">Something went wrong</h2>
              <p className="text-sm text-muted-foreground">
                We couldn't find the checkout session. Please try again or contact support.
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Back to pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-muted-foreground">
          Need help? <Link to="/contact" className="underline hover:text-foreground transition-colors">Contact support</Link>.
        </div>
      </div>
    </div>
  );
}
